import React from 'react'
import { Link } from 'react-router-dom'
import { C, fOutfit, fBody, fMono, PublicNav, PublicFooter, REG_FLAGS, FlagBadge } from '../../Shared/Brand'

const VALUES = [
  { title: 'Automation First', desc: 'Every strategy runs through the Arbitrade Bot — no manual desk, no emotional trading decisions.' },
  { title: 'Transparent Reporting', desc: 'Live balances, profit history, and order hashes are visible in your dashboard at all times.' },
  { title: 'Risk Awareness', desc: 'We surface risk disclosures clearly and never present daily caps as guarantees.' },
]

const LEGAL_LINKS = ['Client Agreement', 'Risk Disclosure', 'Privacy Policy', 'AML Policy', 'Key Information Documents']

function SectionTitle({ eyebrow, title, desc }) {
  return (
    <div style={{ textAlign: 'center', marginBottom: '48px' }}>
      <div style={{ fontSize: '11px', fontFamily: fMono, letterSpacing: '0.14em', color: C.primary, textTransform: 'uppercase', marginBottom: '10px' }}>{eyebrow}</div>
      <h2 style={{ fontFamily: fOutfit, fontWeight: 700, fontSize: 'clamp(28px,4vw,38px)', lineHeight: 1.1, color: C.tx, margin: '0 0 12px' }}>{title}</h2>
      {desc && <p style={{ fontSize: '16px', fontWeight: 500, color: C.tx2, lineHeight: 1.3, maxWidth: '600px', margin: '0 auto' }}>{desc}</p>}
    </div>
  )
}

export default function CompanyPage() {
  return (
    <div style={{ background: C.white, color: C.tx, fontFamily: fBody, minHeight: '100vh' }}>
      <style>{`
        .xc-values-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; }
        .xc-flags-grid { display: flex; flex-wrap: wrap; gap: 24px; justify-content: center; }
        .xc-legal-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; }
        @media (max-width: 640px) { .xc-hero-title { font-size: 30px !important; } }
      `}</style>
      <PublicNav active="Company" />

      {/* HERO — dark */}
      <section style={{ padding: '140px 24px 80px', textAlign: 'center', background: C.dark, color: C.ltx, borderBottom: `1px solid ${C.dbdr}` }}>
        <div style={{ fontSize: '11px', fontFamily: fMono, letterSpacing: '0.14em', color: C.primary, textTransform: 'uppercase', marginBottom: '14px' }}>Company</div>
        <h1 className="xc-hero-title" style={{ fontFamily: fOutfit, fontWeight: 700, fontSize: 'clamp(34px,5vw,52px)', lineHeight: 1.1, margin: '0 0 16px', letterSpacing: '-0.02em' }}>
          About Xperia Capital
        </h1>
        <p style={{ fontSize: '16px', fontWeight: 500, color: C.ltx2, maxWidth: '640px', margin: '0 auto', lineHeight: 1.4 }}>
          Xperia Capital Limited, based in Mauritius, builds automated crypto arbitrage infrastructure so traders can access cross-exchange opportunities without watching the market around the clock.
        </p>
        <Link to="/company/about" style={{ display: 'inline-block', marginTop: '20px', background: C.primary, color: C.dark, fontWeight: 700, fontSize: '13px', padding: '11px 22px', borderRadius: '999px', textDecoration: 'none' }}>Read Full Story</Link>
      </section>

      {/* VALUES — light */}
      <section style={{ padding: '80px 24px', maxWidth: '1200px', margin: '0 auto' }}>
        <SectionTitle eyebrow="What We Stand For" title="Our Approach" />
        <div className="xc-values-grid">
          {VALUES.map(v => (
            <div key={v.title} style={{ background: C.light, border: `1px solid ${C.bdr}`, borderRadius: '14px', padding: '20px' }}>
              <div style={{ fontFamily: fOutfit, fontWeight: 600, fontSize: '17px', color: C.tx, marginBottom: '8px' }}>{v.title}</div>
              <p style={{ fontSize: '14px', color: C.tx2, lineHeight: 1.6, margin: 0 }}>{v.desc}</p>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <Link to="/company/careers" style={{ fontSize: '13px', color: C.primaryDark, textDecoration: 'underline' }}>Join the team →</Link>
        </div>
      </section>

      {/* REGULATION — light, tinted */}
      <section style={{ padding: '80px 24px', borderTop: `1px solid ${C.bdr}`, background: C.light }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <SectionTitle eyebrow="Regulation & Compliance" title="Where We Operate" desc="Xperia Capital maintains registrations and compliance presence across the following jurisdictions." />
          <div className="xc-flags-grid">
            {REG_FLAGS.map(({ country, code }) => (
              <div key={code} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <FlagBadge country={country} size={48} />
                <span style={{ fontSize: '11px', color: C.tx3, fontWeight: 600, letterSpacing: '0.04em' }}>{code}</span>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <Link to="/company/regulation" style={{ fontSize: '13px', color: C.primaryDark, textDecoration: 'underline' }}>Full regulatory details →</Link>
          </div>
        </div>
      </section>

      {/* LEGAL — light */}
      <section style={{ padding: '80px 24px', maxWidth: '900px', margin: '0 auto' }}>
        <SectionTitle eyebrow="Legal" title="Documents & Disclosures" />
        <div className="xc-legal-grid">
          {LEGAL_LINKS.map(l => (
            <Link key={l} to="/company/legal" style={{
              display: 'block', background: C.light, border: `1px solid ${C.bdr}`, borderRadius: '10px',
              padding: '14px 16px', fontSize: '13px', color: C.tx2, textDecoration: 'none', fontWeight: 500,
            }}>{l} →</Link>
          ))}
        </div>
        <p style={{ fontSize: '12px', color: C.tx3, marginTop: '24px', textAlign: 'center', lineHeight: 1.7 }}>
          Trading involves a high level of risk and may result in losses exceeding your initial deposit. Please read our full Risk Disclosure before subscribing to any tier.
        </p>
      </section>

      {/* CONTACT — light, tinted */}
      <section style={{ padding: '60px 24px 100px', borderTop: `1px solid ${C.bdr}`, background: C.light, textAlign: 'center' }}>
        <div style={{ fontSize: '11px', fontFamily: fMono, letterSpacing: '0.14em', color: C.primary, textTransform: 'uppercase', marginBottom: '10px' }}>Contact</div>
        <h2 style={{ fontFamily: fOutfit, fontWeight: 700, fontSize: 'clamp(26px,3.5vw,34px)', lineHeight: 1.1, color: C.tx, margin: '0 0 12px' }}>Get In Touch</h2>
        <p style={{ fontSize: '16px', fontWeight: 500, color: C.tx2, marginBottom: '20px' }}>Open a support ticket from your dashboard's Support page, or reach out via the social channels below.</p>
        <Link to="/company/contact" style={{ display: 'inline-block', border: `1px solid ${C.bdr}`, color: C.tx2, fontWeight: 700, fontSize: '13px', padding: '11px 22px', borderRadius: '999px', textDecoration: 'none' }}>Contact Options</Link>
      </section>

      <PublicFooter />
    </div>
  )
}
